"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Book, Heart, Feather, Globe, Moon, Sun, Sparkles, Crown, Flower, Eye, Shield, Wind } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function OracleCardsPage() {
  const oracleTypes = [
    {
      name: "Angel Oracle Kartları",
      description: "Meleklerden gelen mesajlar ve rehberlik",
      icon: <Feather className="h-6 w-6 text-white" />,
      color: "from-purple-500 to-pink-500",
      themes: ["Koruma", "Rehberlik", "Ruhsal destek", "İlahi mesajlar"],
      examples: ["Doreen Virtue Angel Cards", "Kyle Gray Angel Prayers", "Rebecca Campbell Work Your Light"]
    },
    {
      name: "Goddess Oracle Kartları",
      description: "Tanrıça enerjileri ve kadınsı güç",
      icon: <Crown className="h-6 w-6 text-white" />,
      color: "from-violet-500 to-purple-700",
      themes: ["Kadınsı güç", "Yaratıcılık", "Intuisyon", "Doğal döngüler"],
      examples: ["Amy Sophia Marashinsky Goddess Guidance", "Colette Baron-Reid Goddess Power", "Rebecca Campbell Rise Sister Rise"]
    },
    {
      name: "Doğa Oracle Kartları",
      description: "Doğanın bilgeliği ve elemental güçler",
      icon: <Flower className="h-6 w-6 text-white" />,
      color: "from-purple-400 to-green-600",
      themes: ["Doğal döngüler", "Element güçleri", "Hayvan rehberleri", "Bitki bilgeliği"],
      examples: ["Colette Baron-Reid Wisdom of the Oracle", "Rebecca Campbell Earth Warriors", "Steven Farmer Earth Magic"]
    },
    {
      name: "Chakra Oracle Kartları",
      description: "Enerji merkezleri ve ruhsal iyileşme",
      icon: <Sun className="h-6 w-6 text-white" />,
      color: "from-indigo-500 to-purple-600",
      themes: ["Enerji iyileşme", "Chakra dengeleme", "Ruhsal gelişim", "Meditasyon"],
      examples: ["Colette Baron-Reid The Spirit Animal Oracle", "Rebecca Campbell Starseed Oracle", "Kyle Gray Keepers of the Light"]
    },
    {
      name: "Aşk ve İlişki Oracle",
      description: "Kalp konuları ve ilişki rehberliği",
      icon: <Heart className="h-6 w-6 text-white" />,
      color: "from-pink-500 to-purple-600",
      themes: ["Aşk", "İlişkiler", "Kendini sevme", "Kalp iyileşmesi"],
      examples: ["Colette Baron-Reid Wisdom of the Heart", "Rebecca Campbell Letters to a Starseed", "Kyle Gray Angel Numbers"]
    },
    {
      name: "Mistik Oracle Kartları",
      description: "Gizli bilgiler ve ruhsal sırlar",
      icon: <Eye className="h-6 w-6 text-white" />,
      color: "from-purple-600 to-indigo-700",
      themes: ["Gizli bilgi", "Mistik deneyimler", "Ruhsal uyanış", "İç görü"],
      examples: ["Rebecca Campbell The Starseed Oracle", "Colette Baron-Reid The Good Tarot", "Kyle Gray Angels and Ancestors"]
    }
  ];

  const readingBenefits = [
    {
      title: "Pozitif Odak",
      description: "Oracle kartları genellikle olumlu mesajlar ve çözümler sunar",
      icon: <Sparkles className="h-5 w-5" />
    },
    {
      title: "Kolay Erişim",
      description: "Basit dil ve anlaşılır sembollerle herkes tarafından kullanılabilir",
      icon: <Book className="h-5 w-5" />
    },
    {
      title: "Ruhsal Gelişim",
      description: "İç bilgelik ve sezgisel yetenekleri geliştirmeye yardımcı olur",
      icon: <Star className="h-5 w-5" />
    },
    {
      title: "Günlük Rehberlik",
      description: "Her gün için ilham verici mesajlar ve yönlendirmeler",
      icon: <Sun className="h-5 w-5" />
    }
  ];

  const popularThemes = [
    "Manifestation ve yaratma gücü",
    "Ruhsal uyanış ve gelişim",
    "İç iyileşme ve dönüşüm",
    "Aşk ve ilişki rehberliği",
    "Kariyer ve yaşam amacı",
    "Sezgisel gelişim",
    "Enerji korunması",
    "Melek ve rehber iletişimi",
    "Doğal döngüler ve mevsimler",
    "Chakra dengeleme ve iyileşme"
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-purple-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Oracle</span>
            <span className="text-purple-500">/</span>
            <span className="text-sm">Kartlar</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Tarot Sistemleri
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/oracle"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Oracle Kartları
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Kart Türleri</span>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/20 to-violet-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Book className="h-10 w-10 text-purple-400" />
            Oracle Kart Türleri
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            Oracle kartları, sonsuz çeşitlilikte tema ve yaklaşımla ruhsal rehberlik sunan kartlardır.
            Her deste, benzersiz bir perspektif ve özel mesajlar taşıyarak yaşamımıza ilham verir.
          </p>
        </div>

        {/* Oracle Types */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Oracle Kart Türleri</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {oracleTypes.map((type, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700 hover:border-purple-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${type.color} rounded-lg flex items-center justify-center`}>
                    {type.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100">{type.name}</h3>
                  </div>
                </div>
                <p className="text-slate-300 mb-4">{type.description}</p>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-200 mb-2">Ana Temalar:</h4>
                  <div className="flex flex-wrap gap-2">
                    {type.themes.map((theme, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded text-xs">
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-slate-200 mb-2">Popüler Desteler:</h4>
                  <ul className="space-y-1">
                    {type.examples.map((example, i) => (
                      <li key={i} className="text-slate-400 text-sm">• {example}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reading Benefits */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Oracle Kartlarının Faydaları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {readingBenefits.map((benefit, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center mx-auto mb-4 text-white">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-100 mb-2">{benefit.title}</h3>
                <p className="text-slate-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Themes */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Popüler Oracle Temaları</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {popularThemes.map((theme, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <span className="text-slate-300">{theme}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How to Choose */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Oracle Destesi Nasıl Seçilir?</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Kişisel Tercihler</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Görsel Stil:</strong> Sanat tarzı ve renkler</li>
                <li>• <strong>Tema:</strong> İlgi alanınıza uygun konu</li>
                <li>• <strong>Mesaj Tonu:</strong> Yumuşak vs güçlendirici</li>
                <li>• <strong>Kart Sayısı:</strong> 44-60 arası yaygın</li>
                <li>• <strong>Rehber Kitap:</strong> Detaylı açıklamalar</li>
              </ul>
            </div>
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-3">Kullanım Amacı</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• <strong>Günlük Rehberlik:</strong> Basit ve net mesajlar</li>
                <li>• <strong>Ruhsal Gelişim:</strong> Derin ve dönüştürücü</li>
                <li>• <strong>Spesifik Konular:</strong> Aşk, kariyer, iyileşme</li>
                <li>• <strong>Meditasyon:</strong> Görsel ve sembolik</li>
                <li>• <strong>Öğrenme:</strong> Detaylı açıklamalı</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Reading Techniques */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Oracle Kart Okuma Teknikleri</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Basit Yöntemler</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• <strong>Tek Kart:</strong> Günlük mesaj veya odak</li>
                  <li>• <strong>Üç Kart:</strong> Durum-Eylem-Sonuç</li>
                  <li>• <strong>Haftalık Spread:</strong> 7 kart, her gün için</li>
                  <li>• <strong>Ay Döngüsü:</strong> Yeni ay ve dolunay kartları</li>
                  <li>• <strong>Sezgisel Çekim:</strong> Kaç kart hissediyorsanız</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-100 mb-3">Gelişmiş Yaklaşımlar</h3>
                <ul className="space-y-2 text-slate-300 text-sm">
                  <li>• <strong>Chakra Spread:</strong> 7 kart, her chakra için</li>
                  <li>• <strong>Elemental Spread:</strong> 4 element analizi</li>
                  <li>• <strong>Yıl Döngüsü:</strong> 12 ay için rehberlik</li>
                  <li>• <strong>Meditasyon Kartı:</strong> Derin iç görü için</li>
                  <li>• <strong>Kombinasyon:</strong> Tarot ile birlikte kullanım</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-purple-900/20 border border-purple-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-purple-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-purple-100 leading-relaxed">
            Oracle kartları rehberlik ve ilham amaçlıdır, kehanet değildir. Mesajları kendi yaşamınızla
            ilişkilendirerek sezginizi geliştirmek için kullanın. Önemli kararlar için uzman görüşü alın.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tarot Sistemleri
          </Link>

          <Link
            href="/about/tarot/systems/oracle"
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Oracle Kartları
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}